import { round } from 'lodash'
import { type AccountPlan, type FeaturePair } from './account-plan'
import { FeatureName } from './feature'
import type ProjectForm from './forms/project-form'
import { type Plan } from './plan'
import type Project from './project'
import { TestFramework } from './project'

export class Account {
  id: number
  createdAt: string
  updatedAt: string
  name: string
  display: string
  defectBaseURL: string
  currentAccountPlan?: AccountPlan
  currentPlan?: Plan
  currentPlanFeatures?: FeaturePair[]
  numberOfRunners: number
  windowsRunners: number
  windowsMonthlyPriceOverride: number
  setupComplete: boolean
  monthlyBilling: boolean
  cardBrand: string
  cardLastFour: string
  cardExpireMonth: number
  cardExpireYear: number
  monthlyAiQuotaRemaining: number
  monthlyTestRunAnalysisQuotaRemaining: number
  legacyPlan: boolean
  aiOptOut: boolean

  maxRunners (project: Project | ProjectForm): number {
    return project.testingFramework === TestFramework.NUnit && !project.usingDotnetCore
      ? this.windowsRunners
      : this.numberOfRunners
  }

  maxRunnersByType (runnerType: string): number {
    return runnerType === 'WINDOWS'
      ? this.windowsRunners
      : this.numberOfRunners
  }

  canGetAiSuggestions (): boolean {
    return this.monthlyAiQuotaRemaining != null && this.monthlyAiQuotaRemaining > 0
  }

  canGetTestRunAnalysis (): boolean {
    return !this.aiOptOut && this.monthlyTestRunAnalysisQuotaRemaining != null && this.monthlyTestRunAnalysisQuotaRemaining > 0
  }

  hasFeature (featureName: FeatureName): boolean {
    if (this.currentPlanFeatures != null) {
      return this.currentPlanFeatures.find(featurePair => featurePair.first === featureName) !== undefined
    }
    return false
  }

  getFeature (featureName: FeatureName): FeaturePair | undefined {
    if (this.currentPlanFeatures != null) { return this.currentPlanFeatures.find(featurePair => featurePair.first === featureName) }
    return undefined
  }

  getFeatureValue (featureName: FeatureName): number | undefined {
    const feature = this.getFeature(featureName)
    if (feature != null) {
      return feature.second
    }
    return undefined
  }

  monthlyTestRunAnalysisLimit (): number {
    const featureLimit = this.getFeatureValue(FeatureName.MONTHLY_AI_LIMIT)
    if (featureLimit != null) {
      return featureLimit
    } else {
      return 10 // Protects Legacy Accounts until migration is done
    }
  }

  monthlyTestExecutionHoursLimit (): number {
    const featureLimit = this.getFeatureValue(FeatureName.MAX_TESTING_MINUTES)
    if (featureLimit != null) {
      return round((featureLimit / 60), 0)
    } else {
      return 5 // Protects Legacy Accounts until migration is done
    }
  }

  isLegacyOrTierTwoPlusPlan (): boolean {
    return this.isLegacy() || (this.currentPlan != null && this.currentPlan.tier >= 2)
  }

  isLegacyOrQualifiedTier (requiredTier: number): boolean {
    return this.isLegacy() || (this.currentPlan != null && this.currentPlan.tier >= requiredTier)
  }

  isLegacy (): boolean {
    return this.legacyPlan || (this.currentPlan != null && this.currentPlan.tier === 0)
  }
}
